import { Component, h, Element, State, Listen, Event, EventEmitter, Prop } from '@stencil/core';

interface ListboxOption {
  id: string;
  html: string;
  value: string;
}

export interface ActivatedEvent {
  value: string;
  index: number;
}

/**
 * Offer predictive suggestions while typing your search query.
 *
 * @slot start - Content to display at the start (top).
 */
@Component({
  tag: 'pennlibs-autocomplete',
  styleUrl: 'pennlibs-autocomplete.css',
  shadow: true
})
export class Autocomplete {
  @Element() el!: HTMLElement;

  /**
   * The `id` of the input that this autocomplete is attached to.
   * @prop for
   */
  @Prop() for?: string;

  @State() showSuggestions: boolean = false;
  @State() currentIndex: number = -1;
  @State() originalValue: string = '';
  @State() options: ListboxOption[] = [];

  /**
   * Emitted when a user activates (selects) an autocomplete suggestion.
   * @event pl:activated
   */
  @Event({ eventName: 'pl:activated' }) activated: EventEmitter<ActivatedEvent>;

  private blurTimeout: number;
  private mutationObserver: MutationObserver;
  private readonly keyHandlerMap = {
    'Escape': () => this.handleEscape(),
    'ArrowDown': () => this.handleArrowDown(),
    'ArrowUp': () => this.handleArrowUp(),
    'Enter': () => this.handleEnter()
  };

  componentWillLoad() {
    this.options = this.parseOptionsFromDOM();
  }

  componentDidLoad() {
    this.setupInput();
    this.setupMutationObserver();
  }

  disconnectedCallback() {
    if (this.blurTimeout) clearTimeout(this.blurTimeout);
    if (this.mutationObserver) this.mutationObserver.disconnect();
  }

  private setupMutationObserver(): void {
    this.mutationObserver = new MutationObserver(() => {
      this.options = this.parseOptionsFromDOM();
    });

    this.mutationObserver.observe(this.el, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  private parseOptionsFromDOM(): ListboxOption[] {
    const listbox = this.findListbox();
    if (!listbox) return [];

    const elements = Array.from(listbox.querySelectorAll('[role="option"]')) as HTMLElement[];
    return elements.map((el, i) => this.createOption(el, i));
  }

  private findListbox(): HTMLOListElement | null {
    return Array.from(this.el.children).find(child =>
      child.matches('ol[role="listbox"]')
    ) as HTMLOListElement;
  }

  private createOption(element: HTMLElement, index: number): ListboxOption {
    return {
      id: element.id || `option-${index}`,
      html: element.innerHTML,
      value: element.getAttribute('data-pl-value') || element.textContent?.trim() || ''
    };
  }

  private getInput(): HTMLInputElement | null {
    if (!this.for) return null;

    const slot = this.el.shadowRoot?.querySelector('slot[name="start"]') as HTMLSlotElement;
    if (!slot) return null;

    const assignedElements = slot.assignedElements();
    if (assignedElements.length === 0) return null;

    const input = assignedElements[0].querySelector(`input#${this.for}`) as HTMLInputElement;
    return input || null;
  }

  private isInputFocused(): boolean {
    return this.getInput() === document.activeElement;
  }

  private isTrackedInput(input: HTMLInputElement): boolean {
    return input?.getAttribute('data-pl-autocomplete-connected') === 'true' &&
           this.el.contains(input);
  }

  private setupInput(): void {
    if (!this.for) {
      console.warn('<pennlibs-autocomplete> Missing "for" attribute. Please add for="input-id" to specify which input to attach to.');
      return;
    }

    const input = this.getInput();
    if (!input) {
      console.warn(`<pennlibs-autocomplete> No input element found with id="${this.for}". Ensure an input with this id exists.`);
      return;
    }

    this.setComboboxAttributes(input);
    input.setAttribute('data-pl-autocomplete-connected', 'true');
  }

  private setComboboxAttributes(input: HTMLInputElement): void {
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', 'listbox');
  }

  @Listen('slotchange')
  handleSlotChange() {
    this.options = this.parseOptionsFromDOM();
    this.setupInput();
  }

  @Listen('input', { target: 'body' })
  handleInputEvent(event: Event) {
    const input = event.target as HTMLInputElement;
    if (this.isTrackedInput(input)) {
      this.currentIndex = -1;
      this.openSuggestions();
    }
  }

  @Listen('focus', { target: 'body' })
  handleFocusEvent(event: Event) {
    const input = event.target as HTMLInputElement;
    if (this.isTrackedInput(input)) this.showSuggestionsPanel();
  }

  @Listen('blur', { target: 'body' })
  handleBlurEvent(event: Event) {
    const input = event.target as HTMLInputElement;
    if (this.isTrackedInput(input)) this.deferCloseSuggestions();
  }

  @Listen('focusout')
  handleFocusOut(event: FocusEvent) {
    if (this.isFocusLeavingComponent(event)) this.closeSuggestions();
  }

  @Listen('keydown', { target: 'document' })
  handleKeyDown(event: KeyboardEvent) {
    if (!this.isInputFocused()) return;
    if (event.metaKey || event.ctrlKey) return; // Ignore keyboard shortcuts

    const handler = this.keyHandlerMap[event.key];
    if (handler) {
      // Don't prevent default for Enter - allow form submission
      if (event.key !== 'Enter') {
        event.preventDefault();
      }
      handler();
    }
  }

  private handleEscape() {
    this.reset();
    this.syncAll();
  }

  private handleArrowDown() {
    if (!this.showSuggestions) return;
    this.moveNext();
    this.syncAll();
  }

  private handleArrowUp() {
    if (!this.showSuggestions) return;
    this.movePrevious();
    this.syncAll();
  }

  private handleEnter() {
    if (this.canSelect()) this.selectCurrent();
  }

  // Navigation
  private moveNext(): void {
    const lastIndex = this.options.length - 1;
    this.currentIndex = this.currentIndex < lastIndex ? this.currentIndex + 1 : -1;
  }

  private movePrevious(): void {
    const lastIndex = this.options.length - 1;
    this.currentIndex = this.currentIndex > -1 ? this.currentIndex - 1 : lastIndex;
  }

  private canSelect(): boolean {
    return this.showSuggestions && this.currentIndex >= 0;
  }

  private selectCurrent(): void {
    if (this.currentIndex < 0) return;

    const selectedOption = this.options[this.currentIndex];
    this.applySelection(selectedOption);
    this.emitActivation(selectedOption);
    this.closeSuggestions();
  }

  private applySelection(option: ListboxOption): void {
    const input = this.getInput();
    if (!input) return;

    input.value = option.value;
    this.originalValue = option.value;
  }

  private emitActivation(option: ListboxOption): void {
    this.activated.emit({
      index: this.currentIndex,
      value: option.value
    });
  }

  private handleOptionClick = (index: number): void => {
    this.currentIndex = index;
    this.selectCurrent();
  }

  private openSuggestions(): void {
    if (!this.isInputFocused()) return;

    const input = this.getInput();
    if (input) this.originalValue = input.value;

    this.showSuggestionsPanel();
  }

  private showSuggestionsPanel(): void {
    this.showSuggestions = true;
    this.syncAriaAttributes();
  }

  private closeSuggestions(): void {
    this.reset();
    this.syncAll();
  }

  private deferCloseSuggestions(): void {
    if (this.blurTimeout) clearTimeout(this.blurTimeout);
    this.blurTimeout = setTimeout(() => {
      if (this.isFocusOutsideComponent()) this.closeSuggestions();
    }, 150) as unknown as number;
  }

  private reset(): void {
    this.showSuggestions = false;
    this.currentIndex = -1;
  }

  private isFocusLeavingComponent(event: FocusEvent): boolean {
    const target = event.relatedTarget as HTMLElement;
    return !target ||
           (!this.el.contains(target) && !this.el.shadowRoot?.contains(target));
  }

  private isFocusOutsideComponent(): boolean {
    const active = document.activeElement;
    return !this.el.shadowRoot?.contains(active) && active !== this.getInput();
  }

  private syncAriaAttributes(): void {
    const input = this.getInput();
    if (!input) return;

    this.updateAriaExpanded(input);
    this.updateAriaActiveDescendant(input);
  }

  private syncInputValueToSelection(): void {
    const input = this.getInput();
    if (!input) return;

    input.value = this.currentIndex >= 0 && this.currentIndex < this.options.length
      ? this.options[this.currentIndex].value
      : this.originalValue;
  }

  private syncAll(): void {
    this.syncAriaAttributes();
    this.syncInputValueToSelection();
  }

  private updateAriaExpanded(input: HTMLInputElement): void {
    input.setAttribute('aria-expanded', this.showSuggestions.toString());
  }

  private updateAriaActiveDescendant(input: HTMLInputElement): void {
    const hasSelection = this.showSuggestions &&
                        this.currentIndex >= 0 &&
                        this.currentIndex < this.options.length;
    if (hasSelection) {
      input.setAttribute('aria-activedescendant', this.options[this.currentIndex].id);
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  }

  private shouldShowListbox(): boolean {
    return this.showSuggestions && this.options.length > 0 && this.isInputFocused();
  }

  render() {
    return (
      <div>
        <slot name="start" />
        {this.shouldShowListbox() && (
          <ol role="listbox" id="listbox">
            {this.options.map((option, index) => (
              <li
                role="option"
                id={option.id}
                aria-selected={this.currentIndex === index ? 'true' : 'false'}
                tabindex="-1"
                onClick={() => this.handleOptionClick(index)}
                innerHTML={option.html}
              />
            ))}
          </ol>
        )}
      </div>
    )
  }
}
